import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  DestroyRef,
  inject,
  input,
  model,
  signal
} from '@angular/core';

import { SelectionChangeEvent } from '../../domain/selectable.domain';

import type {
  InputSignal,
  ModelSignal,
  Signal,
  WritableSignal
} from '@angular/core';
import type { SelectableTab, TabsType } from './tabs.domain';

@Component({
  selector: 'td-wb-tab',
  templateUrl: 'tab.component.html',
  styleUrls: ['tab.component.scss'],
  exportAs: 'tdWbTab',
  host: {
    '[attr.aria-label]': 'ariaLabelAttr()',
    '[attr.aria-labelledby]': 'ariaLabelledByAttr()',
    '[class.td-wb-tab--active]': 'active()',
    '[class.td-wb-tab--selected]': 'selectedClass()',
    '[class.td-wb-tab--first]': 'first()',
    '[class.td-wb-tab--primary]': 'primary()',
    '[class.td-wb-tab--primary-large]': 'primaryLarge()',
    '[class.td-wb-tab--secondary]': 'secondary()',
    '[class.td-wb-tab--secondary-compact]': 'secondaryCompact()',
    'class': 'td-wb-tab'
  }
})
export class TabComponent<T> implements SelectableTab {

  readonly tabId: ModelSignal<T> = model<T>(undefined as T);

  get id(): string {
    return this.tabId() as string;
  }

  readonly disabledInput: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  get disabled(): boolean {
    return this.disabledInput();
  }

  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });

  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });

  readonly onSelectionChange: EventEmitter<SelectionChangeEvent<SelectableTab>> = new EventEmitter<SelectionChangeEvent<SelectableTab>>();

  readonly ariaLabelAttr: Signal<string | null> = computed(() => {
    return this.ariaLabelledBy() !== undefined && this.ariaLabelledBy() != null ? null : this.ariaLabel();
  });

  readonly ariaLabelledByAttr: Signal<string | null> = computed(() => {
    return this.ariaLabelledBy() !== undefined && this.ariaLabelledBy() !== null ? this.ariaLabelledBy() : null;
  });

  readonly active: WritableSignal<boolean> = signal(false);

  readonly selectedClass: Signal<boolean> = computed(() => {
    return this.selected() && !this.asyncSelect();
  });

  readonly first: WritableSignal<boolean> = signal(false);

  readonly primary: Signal<boolean> = computed(() => {
    return this.tabsType() === 'primary';
  });

  readonly primaryLarge: Signal<boolean> = computed(() => {
    return this.tabsType() === 'primary-large';
  });

  readonly secondary: Signal<boolean> = computed(() => {
    return this.tabsType() === 'secondary';
  });

  readonly secondaryCompact: Signal<boolean> = computed(() => {
    return this.tabsType() === 'secondary-compact';
  });

  readonly tabsType: WritableSignal<TabsType> = signal<TabsType>(undefined);

  readonly selected: WritableSignal<boolean> = signal(false);
  readonly asyncSelect: WritableSignal<boolean> = signal(false);

  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  getBoundingClientRect(): DOMRect {
    return this.elementRef.nativeElement.getBoundingClientRect();
  }

  /**
   * SelectableTab.select
   */
  select(): void {
    this.setSelected(true);
  }

  /**
   * SelectableTab.deselect
   */
  deselect(emitEvent: boolean = true): void {
    if (this.selected()) {
      this.selected.set(false);
      if (emitEvent) {
        this.emitSelectionChangeEvent();
      }
    }
  }

  /**
   * SelectableTab.setActiveStyles
   */
  setActiveStyles(): void {
    if (!this.active()) {
      this.active.set(true);
    }
  }

  /**
   * SelectableTab.setInactiveStyles
   */
  setInactiveStyles(): void {
    if (this.active()) {
      this.active.set(false);
    }
  }

  setSelected(selected: boolean): void {
    if (this.selected() !== selected) {
      this.selected.set(selected);
      this.emitSelectionChangeEvent();
    }
  }

  setTabId(tabId: T): void {
    this.tabId.set(tabId);
  }

  getTabId(): T {
    return this.tabId();
  }

  isSelected(): boolean {
    return this.selected() === true;
  }

  setFirst(first: boolean): void {
    this.first.set(first);
  }

  setTabsType(tabsType: TabsType): void {
    this.tabsType.set(tabsType);
  }

  onSelect($event: Event): void {
    this.selected.set(true);
    this.emitSelectionChangeEvent(true);
    $event.preventDefault();
  }

  private emitSelectionChangeEvent(fromView: boolean = false): void {
    if (!this.destroyRef.destroyed) {
      this.onSelectionChange.emit(new SelectionChangeEvent(this, fromView));
    }
  }

}
